import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LogoHomeConnect } from '../../../../components/ui/icons/LogoHomeConnect';

/** Shared full-screen auth shell: brand background + centered white card with the
 *  Homère Connect logo. Also normalizes MUI form theming (labels/inputs/helpers)
 *  so Login and Forgot-password read identically. Colours/spacing come from the
 *  theme — no hardcoded values. */
export function AuthCard({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'primary.main',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Box
        sx={theme => ({
          width: '100%',
          maxWidth: theme.authCard.maxWidth,
          bgcolor: 'common.white',
          borderRadius: theme.borderRadius.sm,
          boxShadow: theme.shadows[8],
          p: { xs: 3, sm: 4 },
          '& .MuiTypography-root': { color: theme.palette.text.body },
          '& .MuiInputLabel-root': { color: theme.palette.text.body },
          '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.text.body },
          '& .MuiInputLabel-root.Mui-error': { color: 'error.main' },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: alpha(theme.palette.primary.main, 0.35) },
            '&:hover fieldset': { borderColor: 'primary.main' },
            '&.Mui-focused fieldset': { borderColor: 'primary.main' },
            '&.Mui-error fieldset': { borderColor: 'error.main' },
          },
          '& .MuiInputBase-input': {
            color: theme.palette.text.body,
            '&::placeholder': { color: theme.palette.text.body, opacity: 1 },
          },
          '& .MuiFormHelperText-root': { color: theme.palette.text.label },
          '& .MuiFormHelperText-root.Mui-error': { color: 'error.main' },
        })}
      >
        {/* Branding */}
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <LogoHomeConnect sx={theme => ({ width: theme.authCard.logoWidth, height: 'auto' })} />
        </Box>
        {children}
      </Box>
    </Box>
  );
}
